import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useCart } from '@/context/CartContext';
import { products, categories } from '@/mocks/products';

const CATEGORY_LINKS = [
  { name: 'Footwear', icon: 'ri-run-line', href: '/products?category=Footwear' },
  { name: 'Jackets', icon: 'ri-shirt-line', href: '/products?category=Jackets' },
  { name: 'Tents', icon: 'ri-home-4-line', href: '/products?category=Tents' },
  { name: 'Backpacks', icon: 'ri-shopping-bag-line', href: '/products?category=Backpacks' },
  { name: 'Climbing', icon: 'ri-map-pin-line', href: '/products?category=Climbing' },
  { name: 'Navigation', icon: 'ri-compass-3-line', href: '/products?category=Navigation' },
  { name: 'Sleeping Gear', icon: 'ri-moon-line', href: '/products?category=Sleeping+Gear' },
  { name: 'Cooking', icon: 'ri-fire-line', href: '/products?category=Cooking' },
  { name: 'Lighting', icon: 'ri-flashlight-line', href: '/products?category=Lighting' },
  { name: 'Accessories', icon: 'ri-tools-line', href: '/products?category=Accessories' },
];

const FEATURED_IN_MENU = [
  { label: 'Best Sellers', href: '/products?badge=Best+Seller', icon: 'ri-award-line' },
  { label: 'New Arrivals', href: '/products?badge=New', icon: 'ri-sparkling-line' },
  { label: 'On Sale', href: '/products?badge=Sale', icon: 'ri-price-tag-3-line' },
  { label: 'All Products', href: '/products', icon: 'ri-grid-line' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileShopOpen, setMobileShopOpen] = useState(false);
  const { count, openCart } = useCart();
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';
  const shopRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setShopOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (shopRef.current && !shopRef.current.contains(e.target as Node)) {
        setShopOpen(false);
      }
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchOpen]);

  const solidBg = !isHome || scrolled;

  const searchResults = searchQuery.trim().length > 1
    ? products.filter(p =>
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.category.toLowerCase().includes(searchQuery.toLowerCase())
      ).slice(0, 5)
    : [];

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

  return (
    <>
      {/* Promo Bar */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-[#C4FF61] text-[#1A1A1A] text-xs font-semibold font-outfit text-center py-2 px-4 tracking-wide">
        <span className="hidden sm:inline">FREE SHIPPING on orders over $75 &nbsp;|&nbsp; </span>
        <span>Use code <strong>TRAIL10</strong> for 10% off your first order</span>
        <span className="hidden sm:inline"> &nbsp;|&nbsp; Phoenix, AZ — Est. 2018</span>
      </div>

      <header
        className={`fixed top-8 left-0 right-0 z-50 transition-all duration-300 ${
          solidBg ? 'bg-[#1A1A1A] shadow-lg' : 'bg-transparent'
        }`}
      >
        <div className="px-6 md:px-12 py-3 flex items-center justify-between gap-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 cursor-pointer shrink-0">
            <img
              src="https://public.readdy.ai/ai/img_res/3ccf82e0-7ef8-49c6-b264-45fc943b505b.png"
              alt="SWIFTMARKET LLC"
              className="h-9 w-auto object-contain"
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`px-4 py-2 rounded-md text-sm font-medium tracking-wide transition-colors font-outfit whitespace-nowrap ${
                location.pathname === '/' ? 'text-[#C4FF61]' : 'text-white/80 hover:text-white'
              }`}
            >
              Home
            </Link>

            {/* Shop Mega Menu Trigger */}
            <div ref={shopRef} className="relative">
              <button
                onClick={() => setShopOpen(!shopOpen)}
                className={`flex items-center gap-1 px-4 py-2 rounded-md text-sm font-medium tracking-wide transition-colors font-outfit whitespace-nowrap cursor-pointer ${
                  shopOpen || location.pathname === '/products' ? 'text-[#C4FF61]' : 'text-white/80 hover:text-white'
                }`}
              >
                Shop
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${shopOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {/* Mega Menu */}
              {shopOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[640px] bg-[#1A1A1A] border border-white/10 rounded-xl overflow-hidden shadow-2xl">
                  <div className="grid grid-cols-3 gap-0">
                    {/* Categories */}
                    <div className="col-span-2 p-5 border-r border-white/10">
                      <p className="text-[10px] font-bold tracking-widest text-white/40 uppercase mb-3 font-outfit">Browse Categories</p>
                      <div className="grid grid-cols-2 gap-1">
                        {CATEGORY_LINKS.map((cat) => (
                          <Link
                            key={cat.name}
                            to={cat.href}
                            onClick={() => setShopOpen(false)}
                            className="flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-white/5 transition-colors group"
                          >
                            <div className="w-7 h-7 flex items-center justify-center rounded-md bg-white/5 group-hover:bg-[#C4FF61]/10 transition-colors">
                              <i className={`${cat.icon} text-sm text-white/60 group-hover:text-[#C4FF61] transition-colors`}></i>
                            </div>
                            <span className="text-sm text-white/70 group-hover:text-white transition-colors font-outfit">{cat.name}</span>
                          </Link>
                        ))}
                      </div>
                    </div>

                    {/* Featured */}
                    <div className="p-5">
                      <p className="text-[10px] font-bold tracking-widest text-white/40 uppercase mb-3 font-outfit">Featured</p>
                      <div className="flex flex-col gap-1 mb-5">
                        {FEATURED_IN_MENU.map((item) => (
                          <Link
                            key={item.label}
                            to={item.href}
                            onClick={() => setShopOpen(false)}
                            className="flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-white/5 transition-colors group"
                          >
                            <div className="w-7 h-7 flex items-center justify-center">
                              <i className={`${item.icon} text-sm text-[#C4FF61]/70 group-hover:text-[#C4FF61] transition-colors`}></i>
                            </div>
                            <span className="text-sm text-white/70 group-hover:text-white transition-colors font-outfit">{item.label}</span>
                          </Link>
                        ))}
                      </div>
                      <div className="rounded-lg overflow-hidden relative">
                        <img
                          src="https://public.readdy.ai/ai/img_res/84d8ebf3d6b236b882122f2081928b6f.jpg"
                          alt="Gear"
                          className="w-full h-[100px] object-cover object-top"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-2">
                          <span className="text-[11px] font-bold text-[#C4FF61] font-outfit">SUMMER SALE UP TO 30% OFF</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/products"
              className="px-4 py-2 rounded-md text-sm font-medium tracking-wide transition-colors font-outfit whitespace-nowrap text-white/80 hover:text-white"
            >
              Deals
            </Link>

            <Link
              to="/about"
              className={`px-4 py-2 rounded-md text-sm font-medium tracking-wide transition-colors font-outfit whitespace-nowrap ${
                location.pathname === '/about' ? 'text-[#C4FF61]' : 'text-white/80 hover:text-white'
              }`}
            >
              About
            </Link>

            <a
              href="#contact"
              className="px-4 py-2 rounded-md text-sm font-medium tracking-wide transition-colors font-outfit whitespace-nowrap text-white/80 hover:text-white"
            >
              Contact
            </a>
          </nav>

          {/* Right Actions */}
          <div className="flex items-center gap-1 shrink-0">
            {/* Search */}
            <div ref={searchRef} className="relative">
              <button
                onClick={() => setSearchOpen(!searchOpen)}
                className="flex items-center justify-center w-9 h-9 rounded-md cursor-pointer group hover:bg-white/5 transition-colors"
                aria-label="Search"
              >
                <i className={`text-lg transition-colors ${searchOpen ? 'ri-close-line text-[#C4FF61]' : 'ri-search-line text-white/70 group-hover:text-white'}`}></i>
              </button>

              {/* Search Dropdown */}
              {searchOpen && (
                <div className="absolute top-full right-0 mt-2 w-80 bg-[#1A1A1A] border border-white/10 rounded-xl overflow-hidden shadow-2xl">
                  <form onSubmit={handleSearchSubmit} className="flex items-center gap-2 p-3 border-b border-white/10">
                    <i className="ri-search-line text-white/40 text-sm shrink-0"></i>
                    <input
                      ref={searchInputRef}
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search gear, categories..."
                      className="flex-1 bg-transparent text-white text-sm font-outfit placeholder-white/30 outline-none"
                    />
                    {searchQuery && (
                      <button type="button" onClick={() => setSearchQuery('')} className="cursor-pointer">
                        <i className="ri-close-line text-white/40 hover:text-white text-sm transition-colors"></i>
                      </button>
                    )}
                  </form>

                  {searchResults.length > 0 ? (
                    <div className="py-2">
                      {searchResults.map((p) => (
                        <Link
                          key={p.id}
                          to={`/product/${p.id}`}
                          onClick={() => { setSearchOpen(false); setSearchQuery(''); }}
                          className="flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 transition-colors group"
                        >
                          <img
                            src={p.images[0]}
                            alt={p.name}
                            className="w-10 h-10 rounded-md object-cover object-top shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-white font-outfit truncate group-hover:text-[#C4FF61] transition-colors">{p.name}</p>
                            <p className="text-xs text-white/40 font-outfit">{p.category} · ${p.price}</p>
                          </div>
                          <i className="ri-arrow-right-line text-white/20 group-hover:text-[#C4FF61] text-sm transition-colors"></i>
                        </Link>
                      ))}
                      <div className="px-4 py-2 border-t border-white/10">
                        <button
                          onClick={handleSearchSubmit as unknown as React.MouseEventHandler}
                          className="text-xs text-[#C4FF61] font-outfit hover:underline cursor-pointer"
                        >
                          See all results for &quot;{searchQuery}&quot; →
                        </button>
                      </div>
                    </div>
                  ) : searchQuery.trim().length > 1 ? (
                    <div className="px-4 py-6 text-center">
                      <i className="ri-search-line text-2xl text-white/20 block mb-2"></i>
                      <p className="text-sm text-white/40 font-outfit">No results for &quot;{searchQuery}&quot;</p>
                    </div>
                  ) : (
                    <div className="p-4">
                      <p className="text-[10px] font-bold tracking-widest text-white/30 uppercase mb-2 font-outfit">Popular Searches</p>
                      <div className="flex flex-wrap gap-2">
                        {['Trail Shoes', 'Jackets', 'Tents', 'GPS Watch', 'Backpacks'].map((term) => (
                          <button
                            key={term}
                            onClick={() => setSearchQuery(term)}
                            className="px-3 py-1 rounded-full bg-white/5 hover:bg-white/10 text-xs text-white/60 hover:text-white font-outfit transition-colors cursor-pointer whitespace-nowrap"
                          >
                            {term}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Wishlist */}
            <button
              className="hidden sm:flex items-center justify-center w-9 h-9 rounded-md cursor-pointer group hover:bg-white/5 transition-colors"
              aria-label="Wishlist"
            >
              <i className="ri-heart-line text-lg text-white/70 group-hover:text-white transition-colors"></i>
            </button>

            {/* Cart */}
            <button
              onClick={openCart}
              className="relative flex items-center justify-center w-9 h-9 rounded-md cursor-pointer group hover:bg-white/5 transition-colors"
              aria-label="Open cart"
            >
              <i className="ri-shopping-cart-line text-lg text-white/70 group-hover:text-[#C4FF61] transition-colors"></i>
              {count > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-[#C4FF61] text-[#1A1A1A] text-[9px] font-bold font-outfit rounded-full w-4 h-4 flex items-center justify-center">
                  {count > 99 ? '99+' : count}
                </span>
              )}
            </button>

            {/* Mobile menu toggle */}
            <button
              className="md:hidden flex items-center justify-center w-9 h-9 rounded-md cursor-pointer hover:bg-white/5 transition-colors"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle menu"
            >
              <i className={`text-xl text-white transition-all ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="md:hidden bg-[#1A1A1A] border-t border-white/10 max-h-[80vh] overflow-y-auto">
            <div className="px-5 py-4 flex flex-col gap-1">
              <Link
                to="/"
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/5 text-white/80 hover:text-white text-sm font-medium font-outfit transition-colors"
              >
                <i className="ri-home-line text-base w-5 text-center"></i>
                Home
              </Link>

              {/* Mobile Shop Accordion */}
              <div>
                <button
                  onClick={() => setMobileShopOpen(!mobileShopOpen)}
                  className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg hover:bg-white/5 text-white/80 hover:text-white text-sm font-medium font-outfit transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <i className="ri-store-line text-base w-5 text-center"></i>
                    Shop
                  </div>
                  <i className={`ri-arrow-down-s-line text-base transition-transform ${mobileShopOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {mobileShopOpen && (
                  <div className="ml-8 mt-1 flex flex-col gap-0.5">
                    {CATEGORY_LINKS.map((cat) => (
                      <Link
                        key={cat.name}
                        to={cat.href}
                        onClick={() => setMenuOpen(false)}
                        className="flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-white/5 text-white/60 hover:text-white text-sm font-outfit transition-colors"
                      >
                        <i className={`${cat.icon} text-sm`}></i>
                        {cat.name}
                      </Link>
                    ))}
                    <div className="h-px bg-white/10 my-1"></div>
                    {FEATURED_IN_MENU.map((item) => (
                      <Link
                        key={item.label}
                        to={item.href}
                        onClick={() => setMenuOpen(false)}
                        className="flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-white/5 text-[#C4FF61]/70 hover:text-[#C4FF61] text-sm font-outfit transition-colors"
                      >
                        <i className={`${item.icon} text-sm`}></i>
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <Link
                to="/products"
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/5 text-white/80 hover:text-white text-sm font-medium font-outfit transition-colors"
              >
                <i className="ri-price-tag-3-line text-base w-5 text-center"></i>
                Deals
              </Link>

              <Link
                to="/about"
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/5 text-white/80 hover:text-white text-sm font-medium font-outfit transition-colors"
              >
                <i className="ri-information-line text-base w-5 text-center"></i>
                About
              </Link>

              <a
                href="#contact"
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/5 text-white/80 hover:text-white text-sm font-medium font-outfit transition-colors"
              >
                <i className="ri-mail-line text-base w-5 text-center"></i>
                Contact
              </a>

              <div className="h-px bg-white/10 my-2"></div>

              <button
                onClick={() => { openCart(); setMenuOpen(false); }}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/5 text-white/80 hover:text-white text-sm font-medium font-outfit transition-colors cursor-pointer w-full"
              >
                <i className="ri-shopping-cart-line text-base w-5 text-center"></i>
                Cart {count > 0 && <span className="ml-auto bg-[#C4FF61] text-[#1A1A1A] text-[10px] font-bold rounded-full px-1.5 py-0.5">{count}</span>}
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Spacer for fixed header + promo bar */}
      <div className={`${isHome ? 'h-8' : 'h-[72px]'}`}></div>
    </>
  );
}
